'use client';

import { useState } from 'react';
import { Search } from 'lucide-react';
import type {
  AssetRecord,
  AssetType,
  AssetOwnerType,
  AssetActorType,
  ListAssetsInput,
} from '@impact/assets';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs/tabs';
import { Input } from '@/components/ui/input/input';
import { Button } from '@/components/ui/button/button';
import { ImageCropDialog } from '@/components/image-crop-dialog';
import { AssetUploadButton } from './AssetUploadButton';
import { AssetGalleryPicker } from './AssetGalleryPicker';

interface AssetPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssetSelected: (asset: AssetRecord) => void;
  /** Called with a cropped File when `crop` is set. Parent must upload it. */
  onCroppedFile?: (file: File) => void | Promise<void>;
  assetType?: AssetType;
  ownerType?: AssetOwnerType;
  ownerId?: string;
  tags?: string[];
  uploadedByType?: AssetActorType;
  galleryFilters?: ListAssetsInput;
  title?: string;
  accept?: string;
  selectedAssetId?: string | null;
  crop?: {
    aspect?: number;
    outputSize?: number;
    outputType?: string;
    outputQuality?: number;
  };
}

export function AssetPickerDialog({
  open,
  onOpenChange,
  onAssetSelected,
  onCroppedFile,
  assetType = 'image',
  ownerType,
  ownerId,
  tags,
  uploadedByType = 'dashboard',
  galleryFilters,
  title = 'Select Asset',
  accept,
  selectedAssetId,
  crop,
}: AssetPickerDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [highlighted, setHighlighted] = useState<AssetRecord | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  const filters: ListAssetsInput = {
    status: 'ready',
    uploadedByType: 'dashboard',
    assetType,
    ...(searchQuery ? { q: searchQuery } : {}),
    ...galleryFilters,
  };

  const closeAndReset = () => {
    onOpenChange(false);
    setSearchQuery('');
    setHighlighted(null);
    setCropSrc(null);
    setUploadedAsset(null);
  };

  const [uploadedAsset, setUploadedAsset] = useState<AssetRecord | null>(null);

  const handleUploaded = (asset: AssetRecord) => {
    if (crop && onCroppedFile) {
      setUploadedAsset(asset);
    } else {
      onAssetSelected(asset);
      closeAndReset();
    }
  };

  const handleUploadSelect = () => {
    if (!uploadedAsset) return;
    onAssetSelected(uploadedAsset);
    closeAndReset();
  };

  const handleUploadCrop = () => {
    if (!uploadedAsset?.cdnUrl) return;
    setCropSrc(uploadedAsset.cdnUrl);
  };

  const handleGallerySelect = () => {
    if (!highlighted) return;
    onAssetSelected(highlighted);
    closeAndReset();
  };

  const handleGalleryCrop = () => {
    if (!highlighted?.cdnUrl) return;
    setCropSrc(highlighted.cdnUrl);
  };

  const defaultAccept =
    assetType === 'image'
      ? 'image/jpeg,image/png,image/webp,image/avif,image/gif,image/svg+xml'
      : undefined;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              Upload a new file or select from your asset gallery.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="upload">
            <TabsList className="w-full">
              <TabsTrigger value="upload">Upload</TabsTrigger>
              <TabsTrigger value="gallery">Gallery</TabsTrigger>
            </TabsList>

            <TabsContent value="upload">
              <div className="flex flex-col items-center gap-4 py-6">
                {uploadedAsset ? (
                  <>
                    <div className="w-32 h-32 rounded-md border overflow-hidden bg-muted/50">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={uploadedAsset.cdnUrl}
                        alt="Uploaded preview"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex gap-2">
                      {crop && onCroppedFile && (
                        <Button onClick={handleUploadCrop} size="sm" variant="outline">
                          Crop
                        </Button>
                      )}
                      <Button onClick={handleUploadSelect} size="sm">
                        Select
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setUploadedAsset(null)}
                      className="text-muted-foreground"
                    >
                      Upload a different file
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">Choose a file to upload.</p>
                    <AssetUploadButton
                      assetType={assetType}
                      ownerType={ownerType}
                      ownerId={ownerId}
                      tags={tags}
                      uploadedByType={uploadedByType}
                      accept={accept ?? defaultAccept}
                      buttonLabel="Choose File & Upload"
                      onUploaded={handleUploaded}
                    />
                  </>
                )}
              </div>
            </TabsContent>

            <TabsContent value="gallery">
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search assets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <AssetGalleryPicker
                  filters={filters}
                  selectedAssetId={highlighted?.id ?? selectedAssetId}
                  onSelect={setHighlighted}
                  emptyMessage="No assets found. Try uploading one first."
                />

                <div className="flex justify-end gap-2">
                  {crop && onCroppedFile && (
                    <Button
                      onClick={handleGalleryCrop}
                      disabled={!highlighted}
                      size="sm"
                      variant="outline"
                    >
                      Crop
                    </Button>
                  )}
                  <Button onClick={handleGallerySelect} disabled={!highlighted} size="sm">
                    Select
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {crop && cropSrc && onCroppedFile && (
        <ImageCropDialog
          open
          onOpenChange={(isOpen) => {
            if (!isOpen) setCropSrc(null);
          }}
          imageSrc={cropSrc}
          aspect={crop.aspect}
          outputSize={crop.outputSize}
          outputType={crop.outputType}
          outputQuality={crop.outputQuality}
          onCropped={async (croppedFile) => {
            setCropSrc(null);
            closeAndReset();
            await onCroppedFile(croppedFile);
          }}
        />
      )}
    </>
  );
}
